import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { commissionsApi, type CommissionClaimDetailDto } from "@/api/commissions";
import { ApiError } from "@/api/client";
import { commissionKeys } from "@/hooks/commissions/commissionKeys";
import { Button, FormField } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { currency, percent } from "@/components/commissions/formatters";

interface AffirmModalProps {
  open: boolean;
  onClose: () => void;
  claim: CommissionClaimDetailDto;
}

/**
 * The third exit from Contested: the claimant returns the claim to review while
 * DISAGREEING with the contest, and says why.
 *
 * Shaped on ContestModal rather than on the withdraw ConfirmModal, because the
 * explanation is REQUIRED — it is the claimant's half of the record, held to the
 * same bar as a contest's notes. Deliberately shows the figures being affirmed
 * read-only: this action asserts they are right, so the claimant should be
 * looking at them when they say so.
 */
export function AffirmModal({ open, onClose, claim }: AffirmModalProps) {
  const qc = useQueryClient();
  const [explanation, setExplanation] = useState("");
  const [error, setError] = useState<string | null>(null);
  const c = claim.summary;

  const mutation = useMutation({
    mutationFn: () => commissionsApi.affirm(c.id, { explanation: explanation.trim() }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: commissionKeys.all });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? (e.errors[0] ?? e.message) : String(e)),
  });

  const canSubmit = explanation.trim().length > 0 && !mutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissOnBackdropClick={false}
      title={`Affirm claim ${c.claimNumber}`}
      description="Send the claim back to Accounting unchanged, with your reasons. Nothing on the claim is edited — the activity trail records that you stand by the original figures."
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            loading={mutation.isPending}
            onClick={() => {
              setError(null);
              mutation.mutate();
            }}
          >
            Affirm claim
          </Button>
        </>
      }
    >
      {error && (
        <div role="alert" className="flex items-start gap-space-2 p-space-3 border border-status-error rounded-md bg-status-error/5 text-status-error text-small mb-space-3">
          <AlertTriangle size={16} strokeWidth={1.5} aria-hidden />
          <span>{error}</span>
        </div>
      )}

      <div className="flex flex-col gap-space-4">
        <div>
          <div className="font-display text-label font-semibold uppercase tracking-label text-gold-shadow mb-space-2">
            The figures you are affirming
          </div>
          <dl className="grid grid-cols-3 gap-space-3 m-0">
            <div>
              <dt className="text-caption text-gray-mid m-0">Gross revenue</dt>
              <dd className="text-small tabular-nums text-onyx m-0">{currency(c.grossRevenue)}</dd>
            </div>
            <div>
              <dt className="text-caption text-gray-mid m-0">Commission %</dt>
              <dd className="text-small tabular-nums text-onyx m-0">
                {percent(c.commissionPercent)}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-gray-mid m-0">Commission</dt>
              <dd className="text-small tabular-nums text-onyx m-0">
                {currency(c.commissionAmount)}
              </dd>
            </div>
          </dl>
        </div>

        <FormField
          label="Why the original stands"
          required
          helperText="Accounting sees this on the claim's activity trail. Address the contest notes directly."
        >
          <textarea
            value={explanation}
            onChange={(e) => setExplanation(e.target.value)}
            rows={4}
            className="w-full px-space-3 py-space-2 border border-border-light rounded-md font-body text-small bg-white-pure focus:border-gold focus:outline-none"
          />
        </FormField>

        <p className="text-caption text-gray-mid m-0">
          If you meant to change a figure instead, close this and choose Revise. If you have
          already started editing, finish the revision — a claim with unsaved edits can no longer
          be affirmed as unchanged.
        </p>
      </div>
    </Modal>
  );
}
